import React, { useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../../../config";
import CustomModel from "../../../elements/CustomModel";
import ModelBody from "../../../elements/ModelBody";
import ModelFooter from "../../../elements/ModelFooter";
import CustomForm from "../../../elements/CustomForm";
import CustomButton from "../../../elements/CustomButton";
import {
  METRIC_TYPES,
  buildManualEntryPayload,
  validateManualEntry,
} from "../manualPerformanceHelpers";

// Add-item popup for the Tiered / Non-Tiered Customer Performance widgets.
// Presentational + a single inline POST; all mapping/validation lives in the
// pure helper module. dataType is fixed by the host widget, never chosen here.
const EMPTY = {
  metricType: "",
  contactInfoId: "",
  unitNumber: "",
  jobNumber: "",
  reason: "",
  description: "",
};

const ManualPerformanceModal = ({
  isOpen,
  onClose,
  onSaved,
  token,
  dataType,
  metricTypes,
  reasons = [],
  properties = [],
}) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const metricOptions =
    metricTypes && metricTypes.length ? metricTypes : METRIC_TYPES;
  const propertyOptions = properties.map((p) => ({
    value: p.contactInfoId,
    label: p.propertyName,
  }));

  const setField = (id, value) => setForm((f) => ({ ...f, [id]: value }));

  const close = () => {
    setForm(EMPTY);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const error = validateManualEntry(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    axios
      .post(
        `${API_URL}/Dashboard/api/Dashboard/AddManualPerformanceEntry`,
        buildManualEntryPayload(form, dataType),
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const body = res && res.data;
        if (body && String(body.responsecode) !== "200") {
          toast.error(body.error || "Could not save the entry.");
          return;
        }
        toast.success("Entry added.");
        setForm(EMPTY);
        onClose();
        if (onSaved) onSaved();
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          "Could not save the entry.";
        toast.error(msg);
      })
      .finally(() => setSaving(false));
  };

  return (
    <CustomModel isOpen={isOpen} size="md">
      <CustomForm handleSubmit={handleSubmit} validated={false}>
        <ModelBody>
          <h5 className="text-center mb-3">What would you like to Add?</h5>

          <Form.Group className="mb-3" controlId="metricType">
            <Form.Label className="w-100">
              What would you like to Add?<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={form.metricType}
              onChange={(e) => setField("metricType", e.target.value)}
            >
              <option value="">Select</option>
              {metricOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="contactInfoId">
            <Form.Label className="w-100">
              Property Name<span className="text-danger">*</span>
            </Form.Label>
            <Select
              options={propertyOptions}
              value={
                propertyOptions.find((o) => o.value === form.contactInfoId) ||
                null
              }
              onChange={(opt) =>
                setField("contactInfoId", opt ? opt.value : "")
              }
              isClearable
              placeholder="Select"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="unitNumber">
            <Form.Label className="w-100">Unit Number</Form.Label>
            <Form.Control
              type="text"
              value={form.unitNumber}
              onChange={(e) => setField("unitNumber", e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="jobNumber">
            <Form.Label className="w-100">Job Number</Form.Label>
            <Form.Control
              type="text"
              value={form.jobNumber}
              onChange={(e) => setField("jobNumber", e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="reason">
            <Form.Label className="w-100">Reason</Form.Label>
            <Form.Select
              value={form.reason}
              onChange={(e) => setField("reason", e.target.value)}
            >
              <option value="">Select</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2" controlId="description">
            <Form.Label className="w-100">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </Form.Group>
        </ModelBody>
        <ModelFooter>
          <CustomButton handleClick={close} variant="secondary" type="button">
            Cancel
          </CustomButton>
          <CustomButton variant="primary" type="submit">
            {saving ? <div className="spinner-border"></div> : "Save"}
          </CustomButton>
        </ModelFooter>
      </CustomForm>
    </CustomModel>
  );
};

export default ManualPerformanceModal;
